import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import shiftService from '../services/shiftService';
import { 
  MdSchedule, 
  MdSwapHoriz, 
  MdEdit, 
  MdPeople,
  MdAccessTime,
  MdAssignment
} from 'react-icons/md';

const DutyManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canAccessDutyManagement = ['HRO', 'CHAIRMAN'].includes(user?.role);
  const [stats, setStats] = useState({
    totalShifts: 0,
    totalEmployees: 0,
    assignedEmployees: 0,
    unassignedEmployees: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (canAccessDutyManagement) {
      fetchStats();
    }
  }, [canAccessDutyManagement]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [shiftsResponse, employeesResponse] = await Promise.all([
        shiftService.getAllShifts(),
        shiftService.getHREmployeesWithShifts()
      ]);
      
      if (shiftsResponse.success && employeesResponse.success) {
        const employees = employeesResponse.data;
        const assignedCount = employees.filter(emp => emp.shiftAssignment).length;
        
        setStats({
          totalShifts: shiftsResponse.data.length,
          totalEmployees: employees.length,
          assignedEmployees: assignedCount,
          unassignedEmployees: employees.length - assignedCount
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const dutyModules = [
    {
      id: 'shift-management',
      title: 'Shift Management',
      description: 'Create, edit, and manage work shifts',
      icon: MdSchedule,
      path: '/hro/shift-management',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    },
    {
      id: 'shift-assignment',
      title: 'Shift Assignment',
      description: 'Assign shifts to HR employees',
      icon: MdSwapHoriz,
      path: '/hro/shift-assignment',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600'
    },
    {
      id: 'attendance-correction',
      title: 'Attendance Correction',
      description: 'Review and correct attendance records',
      icon: MdEdit,
      path: '/hro/attendance-correction',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600'
    }
  ];

  if (!canAccessDutyManagement) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to HRO and Chairman.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Duty Management</h1>
          <p className="text-gray-600">Manage work shifts and employee duty schedules</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <MdSchedule className="text-blue-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {loading ? '...' : stats.totalShifts}
                </h3>
                <p className="text-sm text-gray-600">Total Shifts</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <MdPeople className="text-green-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {loading ? '...' : stats.totalEmployees}
                </h3>
                <p className="text-sm text-gray-600">HR Employees</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <MdAssignment className="text-green-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {loading ? '...' : stats.assignedEmployees}
                </h3>
                <p className="text-sm text-gray-600">Assigned</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-full">
                <MdAccessTime className="text-red-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {loading ? '...' : stats.unassignedEmployees}
                </h3>
                <p className="text-sm text-gray-600">Unassigned</p>
              </div>
            </div>
          </div>
        </div>

        {/* Duty Management Modules */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Duty Management Modules</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dutyModules.map((module) => (
              <div
                key={module.id}
                onClick={() => navigate(module.path)}
                className="bg-gray-50 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:bg-gray-100 border-2 border-transparent hover:border-gray-300"
              >
                <div className="flex items-center mb-4">
                  <div className={`p-3 ${module.color} rounded-full`}>
                    <module.icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-4">{module.title}</h3>
                </div>
                <p className="text-gray-600 text-sm">{module.description}</p>
                <div className="mt-4">
                  <span className="text-blue-600 text-sm font-medium hover:text-blue-800">
                    Access Module →
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/hro/shift-management')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Create New Shift
            </button>
            <button
              onClick={() => navigate('/hro/shift-assignment')}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Assign Shifts
            </button>
            <button
              onClick={() => navigate('/hro/attendance-correction')}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              Correct Attendance
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">System Status</h2>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-900">Duty Management System</span>
              </div>
              <span className="text-sm text-green-600 font-medium">Active</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-900">Shift-based Attendance</span>
              </div>
              <span className="text-sm text-blue-600 font-medium">Enabled</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-900">Manual Corrections</span>
              </div>
              <span className="text-sm text-yellow-600 font-medium">Available</span>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DutyManagementPage;